import { type ReactNode, useContext, useEffect, useRef } from 'react';
import {
  type ModalAction,
  type ModalActions,
  ModalList,
  type ModalListItem,
  ModalStep,
  useModalStep,
  useModalSteps,
} from 'src/components/ModalContainer';
import { BulkEditorStepContext } from 'src/pages/CustomViews/components/bulk-editor-step-context';

export const BULK_ACTIONS_TITLE = 'Bulk actions';

interface BulkActionsPickerProps<ItemT extends ModalListItem> {
  options: ItemT[];
  onSelect: (item: ItemT) => void;
}

export const BulkActionsPicker = <ItemT extends ModalListItem>({
  options,
  onSelect,
}: BulkActionsPickerProps<ItemT>) => (
  <ModalList items={options} onSelect={onSelect} testId="bulk-actions-picker" />
);

const BulkEditorStep = () => {
  const editor = useContext(BulkEditorStepContext);
  const { popStep } = useModalStep();
  const isOpen = editor?.isOpen ?? false;

  // The other half of the mirror below: when the modal state closes the editor on its own (a reset
  // on new host context, a finished submit), the step leaves the stack instead of rendering empty.
  // popStep commits through flushSync, which React refuses inside an effect, so it runs a microtask
  // later, the deferral React's own warning recommends.
  useEffect(() => {
    if (!isOpen) {
      queueMicrotask(() => popStep());
    }
  }, [isOpen, popStep]);

  if (!editor) {
    return null;
  }

  const backAction: ModalAction = {
    label: 'Back',
    kind: 'secondary',
    onClick: () => popStep(),
    testId: 'bulk-actions-back',
  };
  // An option list submits on pick, so it keeps only the header's back arrow, no footer.
  const actions: ModalActions | undefined = editor.primaryAction
    ? [backAction, editor.primaryAction]
    : undefined;

  return (
    <ModalStep title={editor.title} testId="bulk-actions-update-dialog" actions={actions}>
      {editor.children}
    </ModalStep>
  );
};

interface BulkActionsRootStepProps {
  isEditorOpen: boolean;
  onEditorClosed: () => void;
  children: ReactNode;
}

export const BulkActionsRootStep = ({
  isEditorOpen,
  onEditorClosed,
  children,
}: BulkActionsRootStepProps) => {
  const { pushStep } = useModalSteps();
  // Strict Mode runs the effect twice on mount, and a re-run on a dep change would push a second
  // editor; the ref keeps the push once per open.
  const isEditorPushedRef = useRef(false);

  // The editor opens from a picked row and from a quick action that names its attribute; mirroring
  // the one state flag into the imperative step stack keeps both on a single path.
  useEffect(() => {
    if (!isEditorOpen || isEditorPushedRef.current) {
      return;
    }
    isEditorPushedRef.current = true;
    const openEditor = async () => {
      await pushStep(<BulkEditorStep />);
      isEditorPushedRef.current = false;
      onEditorClosed();
    };
    void openEditor();
  }, [isEditorOpen, pushStep, onEditorClosed]);

  return (
    <ModalStep title={BULK_ACTIONS_TITLE} testId="bulk-actions-picker">
      {children}
    </ModalStep>
  );
};
